import { useState } from 'react';
import { Tag, Button } from '@carbon/react';
import { Information, ChevronUp, ChevronDown } from '@carbon/icons-react';

/**
 * ExplorerLegend — explains the Knowledge Explorer node badges so the
 * abbreviations are not cryptic (Feedback A8: "KU, KO, %age — what do they
 * mean?"). Kept in sync with ExplorerConceptNode:
 *   - blue "KU"  tag  → grounding_count (knowledge units matched)
 *   - teal "KO"  tag  → ko_count (distinct source documents)
 *   - "%"        tag  → avg_similarity * 100 (match confidence)
 *   - node border     → blue = grounded, gray = not grounded yet
 *
 * Collapsible so it never obscures the graph.
 */
export const ExplorerLegend = () => {
  const [open, setOpen] = useState(true);

  return (
    <div className="explorer-legend" data-testid="explorer-legend">
      <div className="explorer-legend__header">
        <span className="explorer-legend__title">
          <Information size={16} /> Legend
        </span>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          iconDescription={open ? 'Hide legend' : 'Show legend'}
          tooltipPosition="left"
          renderIcon={open ? ChevronUp : ChevronDown}
          onClick={() => setOpen((v) => !v)}
          data-testid="explorer-legend-toggle"
        />
      </div>

      {open && (
        <dl className="explorer-legend__list">
          <div className="explorer-legend__row">
            <Tag size="sm" type="blue">KU</Tag>
            <span>Knowledge Units — passages in your documents matched to this concept.</span>
          </div>
          <div className="explorer-legend__row">
            <Tag size="sm" type="teal">KO</Tag>
            <span>Knowledge Objects — distinct source documents involved.</span>
          </div>
          <div className="explorer-legend__row">
            <Tag size="sm" type="green">%</Tag>
            <span>Match confidence (avg. similarity). Green ≥ 75%, gray &lt; 50%.</span>
          </div>
          <div className="explorer-legend__row">
            <span className="explorer-legend__swatch explorer-legend__swatch--grounded" />
            <span>Blue border = grounded (has matches); gray = not grounded yet.</span>
          </div>
          {/*
            A deduced relation is stored with derivation='cooccurrence': the two instances share a
            knowledge unit and the ontology relates their concepts. The source never states it, so
            the legend says so in as many words — a dotted line on its own is not an explanation.
          */}
          <div className="explorer-legend__row">
            <span className="explorer-legend__swatch explorer-legend__swatch--deduced" />
            <span>
              Dotted orange arrow between two instances = relationship <strong>deduced</strong> from a
              co-occurrence in the same knowledge unit, not asserted by the document. Hover it for
              details.
            </span>
          </div>
        </dl>
      )}
    </div>
  );
};
